<script setup lang="ts">
import { useElementVisibility } from "@vueuse/core";
import MainBreadcrumbs from "../../../ui/components/header/MainBreadcrumbs.vue";
import { onBeforeMount, Ref, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { loadingWrapper } from "@/api/helpers/loadingWrapper";
import { useI18n } from "vue-i18n";
import Card from "../../../ui/components/cards/Card.vue";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
const { t } = useI18n();
import { debounce } from "vue-debounce";

const breadcrumbs = [
	{
		title: "Notifications",
		disabled: true,
		to: "/notifications",
	},
];

const filterTabs: Ref<App.Data.NotificationTypeDO[]> = ref();
const notifications: Ref<App.Data.NotificationDO[]> = ref();

const getMessagesForActiveTab = async () => {
	isLoading.value = true;
	const response = await loadingWrapper(
		useApiClient("notifications").getNotifications(
			activeTab.value,
			activeTab.value !== "unread" ? next_page.value : 1
		)
	);
	if (response.isOk()) {
		setTimeout(() => {
			if (response.value.next_page_url) {
				next_page.value++;
			} else {
				next_page.value = null;
			}
			notifications.value = [...notifications.value, ...response.value.data];
			isLoading.value = false;
		}, 500);
	}
};

const getNotificationFilters = async () => {
	const response = await useApiClient(
		"notifications"
	).getNotificationFilterTabs();

	if (response.isOk()) {
		filterTabs.value = response.value;
		if (response.value.length > 0) {
			activeTab.value = response.value[0].name_clean;
		}
	}
};

onBeforeMount(() => {
	getNotificationFilters();
});

const activeTab = ref();
const next_page = ref(1);
const isLoading = ref(false);

watch(activeTab, (neValue, oldValue) => {
	next_page.value = 1;
	notifications.value = [];

	if (filterTabs.value.length > 0 && oldValue) {
		filterTabs.value[0].notification_count = 0;
	}

	getMessagesForActiveTab();
});

const target = ref(null);
const targetIsVisible = useElementVisibility(target);

watch(targetIsVisible, (newValue) => {
	if (activeTab.value && newValue && next_page.value && !isLoading.value) {
		getMessagesForActiveTab();
	}
});
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" is-user />
	<Card>
		<v-tabs
			v-model="activeTab"
			color="info"
			density="compact"
			align-tabs="end"
			bg-color="surface"
		>
			<v-tab
				v-for="filterTab in filterTabs"
				variant="text"
				selected-class=""
				:color="filterTab.color"
				:value="filterTab.name_clean"
				>{{ t(`notifications.types.${filterTab.name_clean}.title`) }} ({{
					filterTab.notification_count
				}})</v-tab
			>
		</v-tabs>
		<v-window v-model="activeTab">
			<div class="recent-transaction pt-8">
				<div
					v-if="notifications && notifications.length > 0"
					v-for="notification in notifications"
					:key="notification.data?.title"
				>
					<v-row dense class="d-flex mb-4">
						<v-col cols="12">
							<v-alert
								:color="
									!notification.color || notification.color === 'primary'
										? 'blue-grey-darken-3'
										: notification.color
								"
								:icon="
									notification.color === 'error'
										? 'mdi-close-circle-outline'
										: notification.color === 'warning'
										? 'mdi-alert-circle-outline'
										: 'mdi-information-outline'
								"
								variant="tonal"
								border="start"
								dark
							>
								<div class="w-100 d-flex mb-1">
									<div class="font-weight-bold">
										{{ notification.data.title }}
									</div>
									<v-spacer />
									<div class="text-caption">
										{{ notification.created_at }}
									</div>
								</div>
								<div
									class="w-100 notification_body"
									v-html="notification.data.html_message"
								></div>
							</v-alert>
							<div class="w-100 d-flex">
								<v-spacer />
								<div v-if="notification.data.action_route" class="mt-5">
									<v-btn
										:color="notification.color"
										:to="notification.data.action_route"
										variant="tonal"
										density="compact"
									>
										{{ notification.data.action_label }}
									</v-btn>
								</div>
							</div>
						</v-col>
					</v-row>
				</div>
				<div v-if="!isLoading && notifications && notifications.length === 0">
					<v-alert
						border="start"
						color="blue-grey-darken-3"
						variant="tonal"
						icon="mdi-coffee-outline"
						prominent
					>
						{{ t("notifications.empty") }}
					</v-alert>
				</div>
			</div>
		</v-window>
		<div ref="target">
			<v-progress-linear
				v-if="isLoading"
				color="blue-accent"
				indeterminate
				rounded
				height="6"
			/>
		</div>
	</Card>
</template>

<style lang="scss">
.notification_body {
	// add more styles for components from converted markdown
	h1 {
		color: unset !important;
		font-size: 18px;
		font-weight: normal;
	}
}
</style>
